import { f7, useStore } from 'framework7-react';
import { FC } from 'react';
import styles from './RegisterNewMemberStep4Style.module.css';
import { getCarCandidates } from '@/api/registerQRApi';
import RegisterInviteCodeImg from '@/assets/images/register-qr/register-scan.png';
import { Button } from '@/components/uiParts/Button';
import { isApp } from '@/config/device';
import { RegisterUserDataType } from '@/config/initialState';
import { paths } from '@/config/paths';
import { store } from '@/config/store';
import { CarCandidate } from '@/types/api/registerQRApi';
import { storeDispatch } from '@/utils/store';

interface RegisterNewMemberStep4Props {
  hanldeCancel: () => void;
  showSkip?: boolean;
}

export const RegisterNewMemberStep4: FC<RegisterNewMemberStep4Props> = ({
  hanldeCancel,
  showSkip = true,
}) => {
  const registerUserData = useStore(
    store,
    'getRegisterUser',
  ) as RegisterUserDataType;

  const showCarSelectionDialog = (
    candidates: CarCandidate[],
    scanData: Record<string, any>,
  ) => {
    const radioButtons = candidates
      .map(
        (c, i) =>
          `<label class="radio" style="display:flex;align-items:center;margin:8px 0;"><input type="radio" name="car-candidate" value="${i}" ${
            i === 0 ? 'checked' : ''
          }/><i class="icon-radio"></i><span style="margin-left:8px;">${
            c.mk_nm
          } ${c.car_nm}</span></label>`,
      )
      .join('');

    f7.dialog
      .create({
        title: '車両の選択',
        text: 'お乗りのクルマを選択してください',
        content: `<div style="padding:0 16px;">${radioButtons}</div>`,
        buttons: [
          {
            text: '決定',
            bold: true,
            onClick: (_dialog: any, e: any) => {
              const checked = document.querySelector(
                'input[name="car-candidate"]:checked',
              ) as HTMLInputElement | null;
              const idx = checked ? parseInt(checked.value, 10) : 0;
              const selected = candidates[idx];
              storeDispatch('setRegisterUser', 'registerUser', {
                ...registerUserData,
                scan: {
                  ...scanData,
                  carnm_cd: selected.carnm_cd,
                  mk_cd: selected.mk_cd,
                  maker_name: selected.mk_nm,
                  car_name: selected.car_nm,
                },
              });
            },
          },
        ],
      })
      .open();
  };

  window.onInspectionCertificateScan = (data) => {
    try {
      const parsed: {
        transport_bu_name: string;
        class_no: string;
        hiragana: string;
        registry_no: string;
        registry_no_erased?: number;
        businesswork_flg?: number;
        inspection_certificate_file: string | File | Blob; // Base64 image if necessary
        vin: string;
        motor_model?: string;
        katasiki_num?: number;
        ruibetsu_num?: number;
        inspection_limit: string; // YYYY/MM/DD
        model_code: string;
        first_registration: string; // YYYY-MM-DD
      } = JSON.parse(data);

      const baseScanData = {
        ...parsed,
        next_inspection_date: parsed.inspection_limit,
        registration_date: parsed.first_registration,
      };

      if (parsed.model_code) {
        getCarCandidates(parsed.model_code)
          .then((res) => {
            const candidates = res?.data?.candidates ?? [];
            if (candidates.length === 1) {
              const c = candidates[0];
              storeDispatch('setRegisterUser', 'registerUser', {
                ...registerUserData,
                scan: {
                  ...baseScanData,
                  carnm_cd: c.carnm_cd,
                  mk_cd: c.mk_cd,
                  maker_name: c.mk_nm,
                  car_name: c.car_nm,
                },
              });
            } else if (candidates.length >= 2) {
              storeDispatch('setRegisterUser', 'registerUser', {
                ...registerUserData,
                scan: baseScanData,
              });
              f7.view.main.router.navigate(paths.registerNewMemberConfirm);
              showCarSelectionDialog(candidates, baseScanData);
              return;
            } else {
              storeDispatch('setRegisterUser', 'registerUser', {
                ...registerUserData,
                scan: baseScanData,
              });
            }
            f7.view.main.router.navigate(paths.registerNewMemberConfirm);
          })
          .catch(() => {
            storeDispatch('setRegisterUser', 'registerUser', {
              ...registerUserData,
              scan: baseScanData,
            });
            f7.view.main.router.navigate(paths.registerNewMemberConfirm);
          });
      } else {
        storeDispatch('setRegisterUser', 'registerUser', {
          ...registerUserData,
          scan: baseScanData,
        });
        f7.view.main.router.navigate(paths.registerNewMemberConfirm);
      }
    } catch {}
  };

  const openInspectionCertificateScan = () => {
    if (isApp) {
      window.location.href = '/inspection_certificate_reader';
    }
  };

  return (
    <div className={styles['container']}>
      <div className={styles['main-image']}>
        <img src={RegisterInviteCodeImg} />
      </div>
      <div className={styles['content']}>
        <div className={styles['heading']}>
          <h3>車検証の登録を行いましょう</h3>
        </div>
        <div className={styles['button-group']}>
          <Button
            className={styles['button']}
            onClick={openInspectionCertificateScan}
          >
            車検証を登録
          </Button>
          {showSkip && (
            <Button
              className={styles['button']}
              outline
              bgColor="white"
              onClick={hanldeCancel}
            >
              スキップ
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
